import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { dateLabel, todayStr, waLink } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Star, Trash2, MessageCircle, BookOpen, Zap, Printer, BarChart3 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UnderstandingPicker, MaterialHistory } from "@/components/JournalExtras";
import { LaporanPerkembangan } from "@/components/PrintLaporan";
import { currentMonth, monthLabel, UNDERSTANDING } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function RecapTab({ students }) {
  const [month, setMonth] = useState(currentMonth());
  const [studentId, setStudentId] = useState("");
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  useEffect(() => { setData(null); api.get("/journals/recap", { params: { month } }).then((r) => { setData(r.data); if (!studentId && r.data.rows[0]) setStudentId(r.data.rows[0].id); }); }, [month]);
  const row = data?.rows.find((r) => r.id === studentId);
  return (
    <div className="space-y-4">
      <div className="no-print flex flex-wrap items-end gap-3">
        <div><Label className="text-xs text-slate-400">Bulan</Label><Input type="month" data-testid="journal-recap-month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl border-amber-200 w-44 mt-1" /></div>
        <div className="min-w-[220px]"><Label className="text-xs text-slate-400">Siswa</Label>
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger data-testid="journal-recap-student" className="rounded-xl border-amber-200 mt-1"><SelectValue placeholder="Pilih siswa" /></SelectTrigger>
            <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <Button data-testid="print-laporan-btn" onClick={() => window.print()} disabled={!row} className="rounded-xl bg-violet-500 hover:bg-violet-600"><Printer className="w-4 h-4 mr-1" /> Cetak Laporan Perkembangan</Button>
      </div>
      {!data ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> : (
        <>
          <div className="no-print grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.rows.slice(0, 8).map((r) => (
              <button key={r.id} data-testid={`recap-student-${r.id}`} onClick={() => setStudentId(r.id)} className={`p-3 rounded-xl border text-left transition-colors ${r.id === studentId ? "border-violet-400 bg-violet-50" : "border-amber-100 bg-white hover:bg-amber-50"}`}>
                <div className="font-semibold text-slate-800 truncate">{r.name}</div>
                <div className="text-xs text-slate-500">{r.count} pertemuan · ⭐ {r.avg_rating}</div>
                <div className="flex gap-1 mt-1">{Object.entries(r.understanding).map(([k, v]) => v > 0 && <Badge key={k} variant="outline" className={`text-[9px] rounded-full ${UNDERSTANDING[k].cls}`}>{v} {UNDERSTANDING[k].label}</Badge>)}</div>
              </button>
            ))}
          </div>
          <Card className="p-4 sm:p-6 rounded-2xl border-amber-100 overflow-x-auto gemma-scroll">
            <div id="print-root">
              <style>{`@media print { @page { size: 210mm 297mm; margin: 10mm; } }`}</style>
              <LaporanPerkembangan settings={settings} row={row || (studentId ? { ...students.find((s) => s.id === studentId), journals: [], count: 0, avg_rating: 0, understanding: {} } : null)} month={month} />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" data-testid={`star-${i}`} onClick={() => onChange(i)}>
          <Star className={`w-7 h-7 transition-colors ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-200 hover:text-amber-200"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Journal() {
  const [students, setStudents] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [auto, setAuto] = useState(false);
  const [sending, setSending] = useState("");
  const [tab, setTab] = useState("jurnal");
  const [form, setForm] = useState({ student_id: "", date: todayStr(), material: "", homework: "", rating: 5, teacher_note: "", understanding: "paham" });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/students", { params: { status: "aktif" } }),
      api.get("/journals"),
      api.get("/settings"),
    ]).then(([s, j, st]) => { setStudents(s.data); setJournals(j.data); setAuto(!!st.data.fonnte_configured); }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const waAuto = async (j) => {
    setSending(j.id);
    try {
      await api.post(`/wa/journal/${j.id}`);
      toast.success("Laporan jurnal terkirim ke wali ✅");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(""); }
  };

  const save = async () => {
    if (!form.student_id || !form.material.trim()) return toast.error("Pilih siswa & isi materi");
    try {
      const { data } = await api.post("/journals", form);
      if (data.wa_notif?.sent) toast.success("Jurnal tersimpan · laporan WA terkirim ke wali ✅");
      else if (data.wa_notif && data.wa_notif.reason !== "nonaktif") toast.warning(`Jurnal tersimpan, WA gagal: ${data.wa_notif.reason}`);
      else toast.success("Jurnal belajar tersimpan 📚");
      setOpen(false);
      setForm({ student_id: "", date: todayStr(), material: "", homework: "", rating: 5, teacher_note: "", understanding: "paham" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/journals/${id}`); toast.success("Jurnal dihapus"); load(); };

  const waReport = (j) => {
    const st = students.find((s) => s.id === j.student_id);
    if (!st) return;
    const text = `Assalamu'alaikum Bapak/Ibu wali dari ${st.name} 🌟\n\nLaporan belajar hari ini di *Bimbel Gemma*:\n📚 Materi: ${j.material}\n📝 PR/Tugas: ${j.homework || "-"}\n⭐ Penilaian: ${"⭐".repeat(j.rating)}\n\nCatatan Guru: ${j.teacher_note || "-"}\n\nSemangat terus ananda! 💪`;
    window.open(waLink(st.wa, text), "_blank");
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6 animate-float-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Jurnal Belajar Harian</h1>
          <p className="text-slate-500 mt-1">Catat materi, PR, dan penilaian bintang</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-xl bg-amber-50">
              <TabsTrigger value="jurnal" data-testid="tab-jurnal" className="rounded-lg"><BookOpen className="w-4 h-4 mr-1" /> Jurnal</TabsTrigger>
              <TabsTrigger value="rekap" data-testid="tab-rekap-jurnal" className="rounded-lg"><BarChart3 className="w-4 h-4 mr-1" /> Rekap Bulanan</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button data-testid="add-journal-btn" onClick={() => setOpen(true)} className="rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 hover:opacity-90 shadow-md shadow-violet-200">
            <Plus className="w-4 h-4 mr-1" /> Tulis Jurnal
          </Button>
        </div>
      </div>

      {tab === "rekap" ? <RecapTab students={students} /> : journals.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl border-amber-100 text-slate-400">Belum ada jurnal belajar.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {journals.map((j) => {
            const st = students.find((s) => s.id === j.student_id);
            return (
              <Card key={j.id} className="p-5 rounded-2xl border-amber-100 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center"><BookOpen className="w-4 h-4 text-violet-600" /></div>
                    <div>
                      <div className="font-semibold text-slate-900">{st?.name || "Siswa"}</div>
                      <div className="text-xs text-slate-400">{dateLabel(j.date)}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5">{[1,2,3,4,5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= j.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div>
                </div>
                <div className="mt-3 text-slate-800 font-medium">{j.material}</div>
                <Badge variant="outline" className={`text-[10px] rounded-full mt-1 ${UNDERSTANDING[j.understanding || "paham"].cls}`}>{UNDERSTANDING[j.understanding || "paham"].label}</Badge>
                {j.homework && <div className="text-sm text-slate-500 mt-1">📝 PR: {j.homework}</div>}
                {j.teacher_note && <div className="text-sm text-slate-500 mt-1 italic">"{j.teacher_note}"</div>}
                <div className="flex gap-2 mt-3 pt-3 border-t border-amber-50">
                  <Button size="sm" onClick={() => waReport(j)} data-testid={`wa-journal-${j.id}`} variant="outline" className="rounded-lg border-emerald-300 text-emerald-700 h-8"><MessageCircle className="w-4 h-4 mr-1" /> Buka WA</Button>
                  {auto && <Button size="sm" onClick={() => waAuto(j)} disabled={sending === j.id} data-testid={`wa-auto-journal-${j.id}`} className="rounded-lg bg-emerald-500 hover:bg-emerald-600 h-8">{sending === j.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Kirim Otomatis</>}</Button>}
                  <Button size="sm" variant="ghost" onClick={() => remove(j.id)} className="rounded-lg text-rose-600 h-8 ml-auto"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">Tulis Jurnal Belajar</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Siswa</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger data-testid="journal-student-select" className="mt-1 rounded-xl"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl" /></div>
            </div>
            <MaterialHistory studentId={form.student_id} />
            <div><Label>Materi Belajar</Label><Input data-testid="journal-material-input" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} placeholder="cth: Iqro halaman 12" className="mt-1 rounded-xl" /></div>
            <div><Label>PR / Tugas</Label><Input value={form.homework} onChange={(e) => setForm({ ...form, homework: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>Level Pemahaman</Label><div className="mt-1"><UnderstandingPicker value={form.understanding} onChange={(v) => setForm({ ...form, understanding: v })} /></div></div>
            <div><Label>Penilaian</Label><div className="mt-1"><StarPicker value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} /></div></div>
            <div><Label>Catatan Guru</Label><Textarea value={form.teacher_note} onChange={(e) => setForm({ ...form, teacher_note: e.target.value })} className="mt-1 rounded-xl" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Batal</Button>
            <Button data-testid="save-journal-btn" onClick={save} className="rounded-xl bg-violet-500 hover:bg-violet-600">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
